/**
 * Buscador de Propiedades.
 *
 * Antes había además filtros de ciudad, estado y valor (y de sociedad). Con una cartera de
 * pocos pisos no aportaban nada y ocupaban media pantalla: se quedó solo el buscador, en las
 * tres vistas (tarjetas, tabla y mapa).
 *
 * LA BÚSQUEDA VIVE EN LA URL (`?q=`), no en estado local: así un enlace es compartible, el
 * botón atrás funciona y recargar no la pierde. Lleva 300 ms de espera para que cada letra no
 * sea una petición ni una entrada del historial.
 */
import { useEffect, useState } from 'react';
import { Icon } from '../../components/Icon';
import styles from './PropertyFilters.module.css';

export interface PropertyFilterState {
  q: string;
}

export const EMPTY_FILTERS: PropertyFilterState = { q: '' };

interface PropertyFiltersProps {
  value: PropertyFilterState;
  onChange: (next: PropertyFilterState) => void;
  /** Resultados actuales, para decir cuántos hay mientras se busca. */
  resultCount: number;
}

export function PropertyFilters({ value, onChange, resultCount }: PropertyFiltersProps) {
  const [search, setSearch] = useState(value.q);

  useEffect(() => {
    setSearch(value.q);
  }, [value.q]);

  useEffect(() => {
    if (search === value.q) return undefined;
    const timer = setTimeout(() => onChange({ q: search }), 300);
    return () => clearTimeout(timer);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.bar}>
      <div className={styles.row}>
        <div className={styles.search}>
          <span className={styles.searchIcon} aria-hidden="true">
            <Icon name="search" size={16} />
          </span>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Buscar inmueble…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Buscar inmuebles"
          />
        </div>
      </div>

      {value.q && (
        <div className={styles.pills}>
          <span className={styles.pillsLabel}>
            {resultCount} resultado{resultCount === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            className={styles.pill}
            onClick={() => onChange(EMPTY_FILTERS)}
            title="Quitar la búsqueda"
          >
            «{value.q}»
            <span aria-hidden="true">×</span>
          </button>
        </div>
      )}
    </div>
  );
}
